import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useBlockDetails, type BitcoinBlock } from '@/hooks/useBitcoinBlocks';
import { usePublishBlock } from '@/hooks/usePublishBlock';
import { useShareBlock } from '@/hooks/useShareBlock';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Skull, Pickaxe, Database, Clock, Hash, Layers, Share2, Send } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { Button } from '@/components/ui/button';

interface BlockDetailsModalProps {
  block: BitcoinBlock | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BlockDetailsModal({ block, open, onOpenChange }: BlockDetailsModalProps) {
  const { data: details, isLoading } = useBlockDetails(block?.id ?? '');
  const { publishBlock } = usePublishBlock();
  const { shareBlock } = useShareBlock();
  const { user } = useCurrentUser();

  if (!block) return null;

  const timeAgo = formatDistanceToNow(new Date(block.timestamp * 1000), { addSuffix: true });
  const timestamp = format(new Date(block.timestamp * 1000), 'PPpp');

  const handlePublishToNostr = async () => {
    try {
      await publishBlock(block);
    } catch (error) {
      console.error('Failed to publish block:', error);
    }
  };

  const handleShareBlock = async () => {
    try {
      await shareBlock(block);
    } catch (error) {
      console.error('Failed to share block:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] bg-card/95 backdrop-blur-md border-primary/30">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-3 text-2xl font-gothic text-primary">
              <Skull className="h-8 w-8 animate-pulse" />
              Block #{block.height.toLocaleString()}
            </DialogTitle>

            {user && (
              <div className="flex gap-2">
                <Button
                  onClick={handleShareBlock}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  Share as Note
                </Button>
                <Button
                  onClick={handlePublishToNostr}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Share2 className="h-4 w-4" />
                  Publish Block
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="h-[calc(85vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Block Info */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Hash className="h-5 w-5 text-primary" />
                Block Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard
                  icon={<Clock className="h-4 w-4" />}
                  label="Mined"
                  value={timeAgo}
                  subtitle={timestamp}
                />
                <InfoCard
                  icon={<Pickaxe className="h-4 w-4" />}
                  label="Transactions"
                  value={block.tx_count.toLocaleString()}
                />
                <InfoCard
                  icon={<Database className="h-4 w-4" />}
                  label="Size"
                  value={`${(block.size / 1024 / 1024).toFixed(2)} MB`}
                  subtitle={`${(block.weight / 1000).toFixed(0)} kWU`}
                />
                <InfoCard
                  icon={<Layers className="h-4 w-4" />}
                  label="Difficulty"
                  value={block.difficulty.toExponential(2)}
                />
              </div>
            </div>

            {/* Hash Details */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Hash className="h-5 w-5 text-secondary" />
                Cryptographic Details
              </h3>

              <div className="space-y-3">
                <HashDisplay label="Block Hash" hash={block.id} />
                <HashDisplay label="Previous Hash" hash={block.previousblockhash} />
                <HashDisplay label="Merkle Root" hash={block.merkle_root} />
              </div>
            </div>

            {/* Technical Details */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Skull className="h-5 w-5 text-accent" />
                Technical Details
              </h3>

              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoCard label="Nonce" value={block.nonce.toLocaleString()} />
                  <InfoCard label="Bits" value={block.bits.toString(16)} />
                  <InfoCard label="Version" value={`0x${block.version.toString(16)}`} />
                  <InfoCard label="Median Time" value={format(new Date(block.mediantime * 1000), 'PPpp')} />
                </div>
              )}
            </div>

            {/* Spooky decoration */}
            <div className="flex justify-center py-4 opacity-20">
              <div className="text-6xl animate-pulse">⚰️💀⚰️</div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface InfoCardProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
}

function InfoCard({ icon, label, value, subtitle }: InfoCardProps) {
  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border/50 hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-semibold text-foreground text-lg">{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );
}

interface HashDisplayProps {
  label: string;
  hash: string;
}

function HashDisplay({ label, hash }: HashDisplayProps) {
  return (
    <div className="bg-muted/20 rounded-lg p-3 border border-border/50">
      <div className="text-sm text-muted-foreground mb-1">{label}</div>
      <div className="font-mono text-xs text-foreground break-all opacity-80 hover:opacity-100 transition-opacity">
        {hash}
      </div>
    </div>
  );
}
